import { useGetSharesSummary } from "hooks/shares";
import "./CompaniesSummary.scss";
import { useEffect, useMemo, useRef, useState } from "react";

const BASE_CURRENCY = "PLN";

const CompaniesSummary = () => {
  const { data: summary } = useGetSharesSummary();

  const [selectedCurrency, setSelectedCurrency] =
    useState<string>(BASE_CURRENCY);

  const availableCurrencies = useMemo(() => {
    if (!summary) {
      return [BASE_CURRENCY];
    }

    const keys = Object.keys(summary).filter(
      (key) => key !== "combined" && key !== BASE_CURRENCY,
    );

    const preferredOrder = ["EUR", "USD"];

    const orderedPreferred = preferredOrder.filter((c) => keys.includes(c));
    const otherCurrencies = keys
      .filter((c) => !preferredOrder.includes(c))
      .sort();

    return [BASE_CURRENCY, ...orderedPreferred, ...otherCurrencies];
  }, [summary]);

  const currentTotalValue = useMemo(() => {
    if (!summary) return 0;

    const currencySummary = summary[
      selectedCurrency as keyof typeof summary
    ] as { totalValue?: number } | undefined;

    if (!currencySummary) return 0;

    return currencySummary.totalValue ?? 0;
  }, [summary, selectedCurrency]);

  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const currenciesRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = currenciesRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const epsilon = 1;
    setCanScrollLeft(el.scrollLeft > epsilon);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - epsilon);
  };

  const handleCurrencyClick = (currency: string) => {
    setSelectedCurrency(currency);
    const button = buttonRefs.current[currency];
    if (button) {
      button.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  };

  const handleScrollBy = (direction: "left" | "right") => {
    const el = currenciesRef.current;
    if (!el) return;
    const amount = Math.max(80, Math.floor(el.clientWidth * 0.8));
    el.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const currencyLabel = useMemo(() => {
    if (selectedCurrency === BASE_CURRENCY) return "zł";
    if (selectedCurrency === "EUR") return "€";
    if (selectedCurrency === "USD") return "$";
    return selectedCurrency;
  }, [selectedCurrency]);

  useEffect(() => {
    if (!availableCurrencies.includes(selectedCurrency)) {
      setSelectedCurrency(BASE_CURRENCY);
    }
  }, [availableCurrencies, selectedCurrency]);

  useEffect(() => {
    const el = currenciesRef.current;
    if (!el) return;

    updateScrollState();

    const onScroll = () => updateScrollState();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateScrollState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCurrencies.length]);

  return (
    <div className="summary">
      <div className="summary__container">
        <div className="summary__header">
          <h3>Portfel</h3>
          {availableCurrencies.length > 1 && (
            <div
              className={
                canScrollRight
                  ? "summary__currencies-wrapper"
                  : "summary__currencies-wrapper summary__currencies-wrapper--no-fade"
              }
            >
              {canScrollLeft && (
                <button
                  type="button"
                  className="summary__scroll-btn summary__scroll-btn--left"
                  onClick={() => handleScrollBy("left")}
                  aria-label="Przewiń waluty w lewo"
                >
                  ‹
                </button>
              )}
              <div className="summary__currencies" ref={currenciesRef}>
                {availableCurrencies.map((currency) => (
                  <button
                    key={currency}
                    ref={(el) => {
                      buttonRefs.current[currency] = el;
                    }}
                    type="button"
                    className={
                      currency === selectedCurrency
                        ? "summary__currency-tab summary__currency-tab--active"
                        : "summary__currency-tab"
                    }
                    onClick={() => handleCurrencyClick(currency)}
                  >
                    {currency}
                  </button>
                ))}
              </div>
              {canScrollRight && (
                <button
                  type="button"
                  className="summary__scroll-btn summary__scroll-btn--right"
                  onClick={() => handleScrollBy("right")}
                  aria-label="Przewiń waluty w prawo"
                >
                  ›
                </button>
              )}
            </div>
          )}
        </div>
        <p>
          {currentTotalValue}{" "}
          <span className="summary__currency">{currencyLabel}</span>
        </p>
      </div>
      <div className="summary__container">
        <h3>Ilość zakupionych spółek</h3>
        <p>{summary?.combined?.totalCount ?? 0}</p>
      </div>
    </div>
  );
};

export default CompaniesSummary;
